import { useSeoMeta, useHead } from '@unhead/react'
import { useState } from 'react'
import { ExportButtons } from '@/components/ExportButtons'
import { ExportData } from '@/utils/exportUtils'

function SonotubeCalculator() {
  useSeoMeta({
    title: 'Sonotube Calculator | Concrete Volume for Cylindrical Forms',
    description: 'Calculate concrete for sonotube forms. Free sonotube calculator with bag estimates. Enter diameter and height for instant volume calculations.',
    ogTitle: 'Sonotube Calculator | Concrete-Calculator.com',
    ogDescription: 'Calculate concrete volume for sonotube cylindrical forms.',
    ogUrl: 'https://www.concrete-calculator.com/sonotube-calculator/',
    ogType: 'website',
    twitterCard: 'summary_large_image'
  })

  useHead({
    link: [
      { rel: 'canonical', href: 'https://www.concrete-calculator.com/sonotube-calculator/' }
    ]
  })

  const [diameter, setDiameter] = useState('')
  const [height, setHeight] = useState('')
  const [numTubes, setNumTubes] = useState('1')
  const [results, setResults] = useState<{
    cubicFeetPerTube: number
    totalCubicFeet: number
    cubicYards: number
    bags80lb: number
    bags60lb: number
  } | null>(null)

  const calculate = () => {
    const d = parseFloat(diameter)
    const h = parseFloat(height)
    const tubes = parseFloat(numTubes)

    if (isNaN(d) || isNaN(h) || isNaN(tubes) || d <= 0 || h <= 0 || tubes <= 0) {
      return
    }

    // Convert inches to feet
    const radiusFeet = (d / 2) / 12
    const heightFeet = h / 12

    // Volume = π × r² × h
    const volumePerTube = Math.PI * radiusFeet * radiusFeet * heightFeet
    const totalVolume = volumePerTube * tubes
    const cubicYards = totalVolume / 27
    const bags80 = Math.ceil(totalVolume / 0.6)
    const bags60 = Math.ceil(totalVolume / 0.45)

    setResults({
      cubicFeetPerTube: volumePerTube,
      totalCubicFeet: totalVolume,
      cubicYards: cubicYards,
      bags80lb: bags80,
      bags60lb: bags60
    })
  }

  const getExportData = (): ExportData => ({
    title: 'Sonotube Calculator Results',
    inputs: [
      { label: 'Tube Diameter', value: `${diameter} inches` },
      { label: 'Tube Height', value: `${height} inches` },
      { label: 'Number of Tubes', value: numTubes }
    ],
    results: [
      { label: 'Per Tube', value: `${results?.cubicFeetPerTube.toFixed(2)} ft³` },
      { label: 'Total Volume', value: `${results?.totalCubicFeet.toFixed(2)} ft³` },
      { label: 'Cubic Yards', value: `${results?.cubicYards.toFixed(2)} yd³` },
      { label: '80 lb Bags', value: `${results?.bags80lb} bags` },
      { label: '60 lb Bags', value: `${results?.bags60lb} bags` }
    ]
  })

  const reset = () => {
    setDiameter('')
    setHeight('')
    setNumTubes('1')
    setResults(null)
  }

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 style={{ fontSize: '2rem', color: '#4A4A4A', marginBottom: '1rem' }}>
          Sonotube Calculator - Cylindrical Form Volume
        </h1>
        
        <nav aria-label="Calculator Tools" className="mb-8">
          <a href="/" className="text-[#FF8C00] hover:underline">← Back to Home</a>
        </nav>

        {/* Calculator */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-[#4A4A4A] mb-6">Calculate Concrete for Cylindrical Forms</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label htmlFor="diameter" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Tube Diameter (inches)
              </label>
              <input
                id="diameter"
                type="number"
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                placeholder="e.g., 12"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="1"
              />
            </div>

            <div>
              <label htmlFor="height" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Tube Height (inches)
              </label>
              <input
                id="height"
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="e.g., 48"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="0"
                step="1"
              />
            </div>

            <div>
              <label htmlFor="numTubes" className="block text-sm font-semibold text-[#4A4A4A] mb-2">
                Number of Tubes
              </label>
              <input
                id="numTubes"
                type="number"
                value={numTubes}
                onChange={(e) => setNumTubes(e.target.value)}
                placeholder="1"
                className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-md focus:border-[#FF8C00] focus:outline-none"
                min="1"
                step="1"
              />
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button
              onClick={calculate}
              className="flex-1 bg-[#FF8C00] text-white font-bold py-3 px-6 rounded-md hover:bg-[#E67E00] transition-colors text-lg"
            >
              Calculate
            </button>
            <button
              onClick={reset}
              className="flex-1 bg-gray-500 text-white font-bold py-3 px-6 rounded-md hover:bg-gray-600 transition-colors text-lg"
            >
              Reset
            </button>
          </div>

          {results && (
            <div className="bg-gray-50 border-2 border-[#FF8C00] rounded-lg p-6">
              <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
                <h3 className="text-xl font-bold text-[#4A4A4A]">Results</h3>
                <ExportButtons data={getExportData()} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Per Tube</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicFeetPerTube.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Total Volume</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.totalCubicFeet.toFixed(2)} ft³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">Cubic Yards</p>
                  <p className="text-2xl font-bold text-[#4A4A4A]">{results.cubicYards.toFixed(2)} yd³</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200">
                  <p className="text-sm text-gray-600 mb-1">80 lb Bags</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.bags80lb} bags</p>
                </div>
                <div className="bg-white p-4 rounded border border-gray-200 sm:col-span-2">
                  <p className="text-sm text-gray-600 mb-1">60 lb Bags (Alternative)</p>
                  <p className="text-2xl font-bold text-[#FF8C00]">{results.bags60lb} bags</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 text-sm text-gray-600">
            <p className="mb-2">
              <strong>How to use:</strong> Enter the diameter and height of your sonotube in inches. Common sizes are 8", 10", 12" diameter tubes for deck piers and building columns.
            </p>
            <p>
              <strong>Common Applications:</strong> Deck footings, building columns, light posts, and mailbox posts.
            </p>
          </div>
        </div>

        {/* FAQ Section */}
        <section aria-labelledby="faq-heading" className="mt-12">
          <h2 id="faq-heading" className="text-2xl font-bold text-[#4A4A4A] mb-4">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                What size sonotube do I need for a deck?
              </h3>
              <p className="text-gray-700">
                For residential decks, 10-12 inch diameter sonotubes are common. Depth should extend below the frost line (typically 36-48 inches depending on climate). Always check local building codes.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-lg text-[#4A4A4A] mb-2">
                How much concrete for a 12 inch sonotube?
              </h3>
              <p className="text-gray-700">
                A 12-inch diameter × 48-inch tall sonotube requires approximately 3.14 cubic feet of concrete, which equals about 6 bags of 80lb concrete mix.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default SonotubeCalculator
